import {SubmissionScanImageType} from './general.symbols';
import {User} from '../../auth/symbols';


/** Describes available frontend-related submission types. */
export enum submissionType {
  Public = 'public',
  Private = 'private',
  Shared = 'shared',
  Static = 'static',
}

export interface LabelData {
  volume: number;
  value: number;
}

export interface Labels {
  [key: string]: LabelData;
}

export interface Surface {
  id: number;
  label_value: number[];
}

export interface Submission {
  corrected_image?: number;
  surface_set?: Surface[];
  base_surface?: number;
  left_ct_processed?: number;
  right_ct_processed?: number;
  left_ct_harmonized?: number;
  right_ct_harmonized?: number;
  scan_date?: string;
  person_weight_unit?: number;
  person_weight?: number;
  person_height_unit?: number;
  person_height1?: number;
  person_height2?: number;
  generate_gif?: boolean;
  reason?: string;
  display_name?: string;
  is_public?: boolean;
  brain_age_derivative?: number;
  id?: string;
  base_image?: number;
  processed_image?: number;
  harmonized_image?: number;

  /** Contain related to submission scan general status. */
  status?: SubmissionGeneralStatus;

  /** Contain related to submission scan detailed status. */
  status_description?: SubmissionDetailedStatus;
  user?: User['uid'];
  age_at_scan?: number;

  /** Indicates if the main BrainKey PDF report is generated and can be viewed. */
  pdf_ready?: boolean;

  /** Indicates if KeyLayer PDF report is generated and can be viewed. */
  key_layer_ready?: boolean;

  /** Indicates if a Executive PDF report is generated and can be viewed. */
  executive_ready?: boolean;

  /** Indicates if a Hyperintensities PDF report is generated and can be viewed. */
  white_matter_ready?: boolean;

  /** Indicates if a Perivascular Spaces PDF report is generated and can be viewed. */
  pvs_ready?: boolean;

  /** Indicates if a Submission Summary PDF report is generated and can be viewed. */
  submission_summary_ready?: boolean;

  /**
   * Indicates if "All Reports" PDF report is generated and can be viewed.
   * "All Reports" - just a name of the report that contains four reports: MRI, KeyLayer, Hyperintensities, Perivascular Spaces.
   */
  all_reports_ready?: boolean;

  /** Indicates if the expanded ventricle results report is generated and can be viewed. */
  evr_ready?: boolean;

  /**
   * Contains is approved statuses for related scan images.
   */
  related_scans_approved?: SubmissionRelatedScans;

  /**
   * Contains a related key layer scan version.
   */
  version?: SubmissionKeyLayerScanVersion['id'];

  /**
   * Contains the id of the derivative used for the AR image.
   */
  ar_image?: number;

  results?: SubmissionResult[];
  is_shared?: boolean;
  results_inspected?: boolean;
  is_owner_charged?: boolean;


  /**
   * Describes the quality of the T1 scan image.
   * Where 0 is the best quality, 2 is acceptable, and >2 is bad quality (reports and charts usually are not generated).
   */
  scan_quality?: SubmissionScanQuality;

  /**
   * Indicates whether the scan was made with a contrast agent.
   * They should be threatened differently in the processing, and the results are probably not reliable.
   * So, if the scan is marked as contrast, it usually threatened as a bad quality scan.
   */
  contrast?: boolean;
  sex?: SubmissionSex;
  data?: {user_metadata?: {scan_type?: string}};
}

/**
 * Describes a possible statuses for the submission.
 */
export enum SubmissionGeneralStatus {
  /** Submission is uploaded and is somewhere before the applying analysis (file type transformation, queue, etc.). */
  new = 1,

  /** Submission is in the processing - analysis is running. */
  processing = 2,

  /** Submission is processed, approved by editors (or automatically) and ready for the review. */
  approved = 3,

  /** Some error occurred during the submission processing. */
  error = 4,
}

/**
 * Describes a possible statuses for the submission.
 * Provides more detailed statuses than the general statuses.
 * Detailed status always starts with the general status number.
 *
 * For example, for error in runners, the detailed status will be 42, where 4 is the general status and 2 is the detailed status.
 * 4 represents the error status, 2 represents the sub-status for the error in runners.
 */
export enum SubmissionDetailedStatus {
  /**
   * The scan is uploaded to the system, but the pipeline is not yet defined.
   */
  newNonPipeline = 10,

  /**
   * The scan is uploaded to the system and ready for processing.
   */
  newPipeline = 11,

  /**
   * The scan is being labeled.
   */
  processingLabeled = 21,

  /**
   * The scan is being linked.
   */
  processingLinked = 22,

  /**
   * The scan is being processed by the runners.
   */
  processingRunners = 23,

  /**
   * The scan is being edited.
   */
  processingEdited = 24,

  /**
   * The processing of the scan is finished.
   */
  processingFinished = 25,

  /**
   * The scan is sent to the editor with the priority 3.
   */
  processingSentToEditor3 = 26,

  /**
   * The scan is sent to the editor with the priority 2.
   */
  processingSentToEditor2 = 27,

  /**
   * The scan is sent to the editor with the priority 1.
   */
  processingSentToEditor1 = 28,

  /**
   * The scan is checked.
   */
  processingChecked = 29,

  /**
   * The processing started for the scan.
   */
  processingForProcessing = 210,

  /**
   * The scan is approved and ready to be viewed by the user.
   */
  approvedGeneral = 31,

  /** Unexpected error which cannot be classified occurred during the submission processing. */
  errorUnknown = 41,

  /** Error occurred in runners. */
  errorInRunners = 42,

  /** Error occurred in processing and processing was halted. */
  errorProcessingHalted = 43,

  /** The submission was rejected because of the low quality. */
  errorRejectedByQuality = 44,

  /** The submission was rejected because of the wrong format. */
  errorRejectedByFormat = 45,

  /** The submission was rejected, but no reason provided. */
  errorRejectedByUnknownReason = 46,
}

/**
 * Describes a possible options for the submission sex field.
 */
export enum SubmissionSex {
  Male = 'M',
  Female = 'F',
}

/**
 * Describes a data format for the related scan derivative IDs and detailed statuses.
 */
export interface SubmissionRelatedScans {
  T2: SubmissionRelatedScanDerivativesAndStatuses;
  Flair: SubmissionRelatedScanDerivativesAndStatuses;
  TOF: SubmissionRelatedScanDerivativesAndStatuses;
  SWI: SubmissionRelatedScanDerivativesAndStatuses;
}

/**
 * Describes a data format for the related scan derivative IDs and detailed statuses.
 */
export interface SubmissionRelatedScanDerivativesAndStatuses {
  /** Contains id of the base scan image (non-processed). */
  base: number;

  /**
   * Contains id of the processed scan image (contains regions).
   * This scan image is the result of applying runners on the base scan image.
   * It is used for displaying selected regions (labels) on the scan image view.
   * Should be used only if the corrected scan image is not presented.
   */
  processed: number;

  /**
   * Contains id of the corrected scan image (contains regions).
   * This scan image is the result of the manual processed scan image editing.
   * It is used for displaying selected regions (labels) on the scan image view.
   * Should have the higher priority than the processed scan image.
   */
  corrected: number;

  /**
   * Detailed status for the current scan image.
   */
  status_description: SubmissionDetailedStatus;

  /**
   * Describes the quality of the related scan image.
   * Where 0 is the best quality, 2 is acceptable, and >2 is bad quality (reports and charts usually are not generated).
   */
  qs: SubmissionScanQuality;
}

export interface SubmissionImageFile extends Blob {
  name: string;
  metadata?: SubmissionScanImageMetadata;
}

export interface SubmissionSurfaceFile extends Blob {
  name: string;
  labels: number[];
}

export interface SubmissionResult {
  id: number;
  is_report: boolean;
}

/** Define a submission type depend on provided data. */
export const defineSubmissionType = (submission: Submission): submissionType => {
  if (!submission?.is_public && submission?.is_shared) {
    return submissionType.Shared;
  } else if (submission?.is_public && !submission?.is_shared) {
    return submissionType.Public;
  }
  return submissionType.Private;
};

/**
 * Describes a submission key layer scan version metadata.
 */
export interface SubmissionKeyLayerScanVersion {
  /** Unique identifier of the key layer scan version. */
  id: number;
}

/**
 * Describes metadata fields for submission scan image.
 */
export interface SubmissionScanImageMetadata {
  /** Unique identifier of the related derivative. */
  id: number;

  /** Api for retrieving the image file. */
  apiEndpoint: string;

  /** Type of the scan image. */
  scanType: SubmissionScanImageType;

  /** Type of the derivative applied to the scan. */
  derivativeType: SubmissionScanImageDerivativeType;

  /** Name of the scan image. */
  name: string;

  /**
   * The minimum value of the label for the scan image.
   * This value is used for the Papaya viewer to set the minimum value of the label.
   */
  minLabelValue?: number;

  /**
   * The maximum value of the label for the scan image.
   * This value is used for the Papaya viewer to set the maximum value of the label.
   */
  maxLabelValue?: number;
}

/**
 * Describes possible derivative types for the scan images.
 */
export enum SubmissionScanImageDerivativeType {
  /** Base scan image; no derivative applied. */
  base = 'base',

  /** Labeled scan image; derivative applied. */
  labeled = 'labeled',
}

/**
 * Describes property name for storing metadata in the Papaya viewer.
 * Used for understanding what scan images are presented in the papaya viewer.
 */
export const propertyNameForStoringMetadataInPapaya = '__metadata';

/**
 * Define metadata for the T1 base scan image presented in the submission.
 */
export const defineSubmissionT1BaseScanImageMetadata = (submission: Submission): SubmissionScanImageMetadata[] => {
  // Define scan part for the endpoint.
  const scanPart = submission?.is_public ? 'public_scan' : 'scan';

  // Define base T1 image metadata.
  // Consider it always presented, otherwise the whole submission will not be shown.
  const baseT1ImageMetadata: SubmissionScanImageMetadata = {
    id: submission.base_image,
    apiEndpoint: `/api/research/${scanPart}/show-decompressed/${submission.base_image}/`,
    scanType: SubmissionScanImageType.t1,
    derivativeType: SubmissionScanImageDerivativeType.base,
    name: `${SubmissionScanImageType.t1}_base`,
  };

  // Return the array of scan images metadata.
  return [baseT1ImageMetadata];
}

/**
 * Define metadata for the T1 label scan image presented in the submission.
 */
export const defineSubmissionT1LabelScanImageMetadata = (submission: Submission): SubmissionScanImageMetadata[] => {
  // Define scan part for the endpoint.
  const scanPart = submission?.is_public ? 'public_scan' : 'scan';

  // If the harmonized T1 image is presented, define its metadata.
  // Otherwise, try to define metadata for the processed T1 image.
  const labeledImage = submission.harmonized_image || submission.processed_image;
  const endPointParam = submission.harmonized_image ? 'harmonized' : 'processed';
  if (labeledImage) {
    const labeledT1ImageMetadata: SubmissionScanImageMetadata = {
      id: labeledImage,
      apiEndpoint: `/api/research/${scanPart}/show/${labeledImage}/${endPointParam}/`,
      scanType: SubmissionScanImageType.t1,
      derivativeType: SubmissionScanImageDerivativeType.labeled,
      name: `${SubmissionScanImageType.t1}_labeled`,
    };

    // Return the array of scan images metadata.
    return [labeledT1ImageMetadata];
  }

  return [];
}

/**
 * Define metadata for the T2 base scan image presented in the submission.
 */
export const defineSubmissionT2BaseScanImageMetadata = (submission: Submission): SubmissionScanImageMetadata[] => {
  // Define scan part for the endpoint.
  const scanPart = submission?.is_public ? 'public_scan' : 'scan';

  // Extract data for the T2 related scan images.
  const t2ImageRelatedData = submission.related_scans_approved?.T2;

  // If the T2 base image is presented, define its metadata.
  if (t2ImageRelatedData?.base) {
    const t2ImageMetadata: SubmissionScanImageMetadata = {
      id: t2ImageRelatedData.base,
      apiEndpoint: `/api/research/${scanPart}/show-decompressed/${t2ImageRelatedData.base}/`,
      scanType: SubmissionScanImageType.t2,
      derivativeType: SubmissionScanImageDerivativeType.base,
      name: `${SubmissionScanImageType.t2}_base`,
    };
    // Return the array of scan images metadata.
    return [t2ImageMetadata];
  }

  return [];
}

/**
 * Define metadata for the T2 label scan image presented in the submission.
 */
export const defineSubmissionT2LabelScanImageMetadata = (submission: Submission): SubmissionScanImageMetadata[] => {
  // Define scan part for the endpoint.
  const scanPart = submission?.is_public ? 'public_scan' : 'scan';

  // Extract data for the T2 related scan images.
  const t2ImageRelatedData = submission.related_scans_approved?.T2;

  // If the T2 base image is presented, define its metadata.
  if (t2ImageRelatedData?.base) {
    // If the corrected or processed T2 image is presented, define its metadata.
    const labeledImage = t2ImageRelatedData.corrected || t2ImageRelatedData.processed;

    // If the labeled T2 image is presented, define its metadata.
    if (labeledImage) {
      // Define endpoint parameter for the labeled T2 image.
      const endPointParam = t2ImageRelatedData.corrected ? 'corrected' : 'processed-simple';

      const labeledT2ImageMetadata: SubmissionScanImageMetadata = {
        id: labeledImage,
        apiEndpoint: `/api/research/${scanPart}/show/${labeledImage}/${endPointParam}/`,
        scanType: SubmissionScanImageType.t2,
        derivativeType: SubmissionScanImageDerivativeType.labeled,
        name: `${SubmissionScanImageType.t2}_labeled`,
      };

      // Return the array of scan images metadata.
      return [labeledT2ImageMetadata];
    }
  }

  return [];
}

/**
 * Define metadata for the Flair base scan image presented in the submission.
 */
export const defineSubmissionFlairBaseScanImageMetadata = (submission: Submission): SubmissionScanImageMetadata[] => {
  // Define scan part for the endpoint.
  const scanPart = submission?.is_public ? 'public_scan' : 'scan';

  // Extract data for the Flair-related scan images.
  const flairImageRelatedData = submission.related_scans_approved?.Flair;

  // If the Flair base image is presented, define its metadata.
  if (flairImageRelatedData?.base) {
    const flairImageMetadata: SubmissionScanImageMetadata = {
      id: flairImageRelatedData.base,
      apiEndpoint: `/api/research/${scanPart}/show-decompressed/${flairImageRelatedData.base}/`,
      scanType: SubmissionScanImageType.flair,
      derivativeType: SubmissionScanImageDerivativeType.base,
      name: `${SubmissionScanImageType.flair}_base`,
    };
    // Return the array of scan images metadata.
    return [flairImageMetadata];
  }

  return [];
}

/**
 * Define metadata for the Flair label scan image presented in the submission.
 */
export const defineSubmissionFlairLabelScanImageMetadata = (submission: Submission): SubmissionScanImageMetadata[] => {
  // Define scan part for the endpoint.
  const scanPart = submission?.is_public ? 'public_scan' : 'scan';

  // Extract data for the Flair-related scan images.
  const flairImageRelatedData = submission.related_scans_approved?.Flair;

  // If the Flair base image is presented, define its metadata.
  if (flairImageRelatedData?.base) {
    // If the corrected or processed Flair image is presented, define its metadata.
    const labeledImage = flairImageRelatedData.corrected || flairImageRelatedData.processed;

    // If the labeled Flair image is presented, define its metadata.
    if (labeledImage) {
      // Define endpoint parameter for the labeled Flair image.
      const endPointParam = flairImageRelatedData.corrected ? 'corrected' : 'processed-simple';

      const labeledFlairImageMetadata: SubmissionScanImageMetadata = {
        id: labeledImage,
        apiEndpoint: `/api/research/${scanPart}/show/${labeledImage}/${endPointParam}/`,
        scanType: SubmissionScanImageType.flair,
        derivativeType: SubmissionScanImageDerivativeType.labeled,
        name: `${SubmissionScanImageType.flair}_labeled`,
      };
      // Return the array of scan images metadata.
      return [labeledFlairImageMetadata];
    }
  }

  return [];
}

/**
 * Define metadata for the TOF base scan image presented in the submission.
 */
export const defineSubmissionTofBaseScanImageMetadata = (submission: Submission): SubmissionScanImageMetadata[] => {
  // Define scan part for the endpoint.
  const scanPart = submission?.is_public ? 'public_scan' : 'scan';

  // Extract data for the TOF-related scan images.
  const tofImageRelatedData = submission.related_scans_approved?.TOF;

  // If the TOF base image is presented, define its metadata.
  if (tofImageRelatedData?.base) {
    const tofImageMetadata: SubmissionScanImageMetadata = {
      id: tofImageRelatedData.base,
      apiEndpoint: `/api/research/${scanPart}/show-decompressed/${tofImageRelatedData.base}/`,
      scanType: SubmissionScanImageType.tof,
      derivativeType: SubmissionScanImageDerivativeType.base,
      name: `${SubmissionScanImageType.tof}_base`,
    };
    // Return the array of scan images metadata.
    return [tofImageMetadata];
  }

  return [];
}

/**
 * Define metadata for the TOF label scan image presented in the submission.
 */
export const defineSubmissionTofLabelScanImageMetadata = (submission: Submission): SubmissionScanImageMetadata[] => {
  // Define scan part for the endpoint.
  const scanPart = submission?.is_public ? 'public_scan' : 'scan';

  // Extract data for the TOF-related scan images.
  const tofImageRelatedData = submission.related_scans_approved?.TOF;

  // If the TOF base image is presented, define its metadata.
  if (tofImageRelatedData?.base) {
    // If the corrected or processed TOF image is presented, define its metadata.
    const labeledImage = tofImageRelatedData.corrected || tofImageRelatedData.processed;

    // If the labeled TOF image is presented, define its metadata.
    if (labeledImage) {
      // Define endpoint parameter for the labeled TOF image.
      const endPointParam = tofImageRelatedData.corrected ? 'corrected' : 'processed-simple';

      const labeledTofImageMetadata: SubmissionScanImageMetadata = {
        id: labeledImage,
        apiEndpoint: `/api/research/${scanPart}/show/${labeledImage}/${endPointParam}/`,
        scanType: SubmissionScanImageType.tof,
        derivativeType: SubmissionScanImageDerivativeType.labeled,
        name: `${SubmissionScanImageType.tof}_labeled`,
      };

      // Return the array of scan images metadata.
      return [labeledTofImageMetadata];
    }
  }

  return [];
}

/**
 * Define metadata for the SWI base scan image presented in the submission.
 */
export const defineSubmissionSwiBaseScanImageMetadata = (submission: Submission): SubmissionScanImageMetadata[] => {
  // Define scan part for the endpoint.
  const scanPart = submission?.is_public ? 'public_scan' : 'scan';

  // Extract data for the SWI-related scan images.
  const swiImageRelatedData = submission.related_scans_approved?.SWI;

  // If the SWI base image is presented, define its metadata.
  if (swiImageRelatedData?.base) {
    const swiImageMetadata: SubmissionScanImageMetadata = {
      id: swiImageRelatedData.base,
      apiEndpoint: `/api/research/${scanPart}/show-decompressed/${swiImageRelatedData.base}/`,
      scanType: SubmissionScanImageType.swi,
      derivativeType: SubmissionScanImageDerivativeType.base,
      name: `${SubmissionScanImageType.swi}_base`,
    };
    // Return the array of scan images metadata.
    return [swiImageMetadata];
  }

  return [];
}

/**
 * Define metadata for the SWI label scan image presented in the submission.
 */
export const defineSubmissionSwiLabelScanImageMetadata = (submission: Submission): SubmissionScanImageMetadata[] => {
  // Define scan part for the endpoint.
  const scanPart = submission?.is_public ? 'public_scan' : 'scan';

  // Extract data for the SWI-related scan images.
  const swiImageRelatedData = submission.related_scans_approved?.SWI;

  // If the SWI base image is presented, define its metadata.
  if (swiImageRelatedData?.base) {
    // If the corrected or processed SWI image is presented, define its metadata.
    const labeledImage = swiImageRelatedData.corrected || swiImageRelatedData.processed;

    // If the labeled SWI image is presented, define its metadata.
    if (labeledImage) {
      // Define endpoint parameter for the labeled SWI image.
      const endPointParam = swiImageRelatedData.corrected ? 'corrected' : 'processed-simple';

      const labeledSwiImageMetadata: SubmissionScanImageMetadata = {
        id: labeledImage,
        apiEndpoint: `/api/research/${scanPart}/show/${labeledImage}/${endPointParam}/`,
        scanType: SubmissionScanImageType.swi,
        derivativeType: SubmissionScanImageDerivativeType.labeled,
        name: `${SubmissionScanImageType.swi}_labeled`,
      };

      // Return the array of scan images metadata.
      return [labeledSwiImageMetadata];
    }
  }

  return [];
}


/**
 * Available statuses of condition for loaded submission data.
 */
export enum AvailableSubmissionLoadStatuses {
  /** Indicates that unexpected error appeared during a submission load. */
  ERROR_DURING_LOADING = 'ERROR_DURING_LOADING',

  /** Indicates that analysis for the submission is not finished yet. */
  ANALYSIS_IN_PROGRESS = 'ANALYSIS_IN_PROGRESS',

  /** Indicates that analysis for the submission is finished with errors. */
  ANALYSIS_ERROR = 'ANALYSIS_ERROR',

  /** Indicates that submission data has no known errors and might be displayed. */
  NO_ERRORS = 'NO_ERRORS',
}

/**
 * Describes the quality of the scan image.
 * Where 0 is the best quality, 2 is acceptable, and >2 is bad quality (reports and charts usually are not generated).
 */
export enum SubmissionScanQuality {
  best = 0,
  good = 1,
  acceptable = 2,
  bad = 3,
  veryBad = 4,
  worst = 5,
}

/**
 * Defines the threshold for the acceptable scan quality.
 */
export const acceptableScanQualityThreshold = SubmissionScanQuality.acceptable;

/**
 * Extracts the scan quality for the provided submission and scan type.
 * Lacking data will be considered as the worst quality.
 */
export const getScanQualityForScanType = (submission: Submission, scanType: SubmissionScanImageType): SubmissionScanQuality => {
  // If the submission is not provided, consider it as the worst quality.
  if (!submission) {
    return SubmissionScanQuality.worst;
  }

  const relatedScansApproved = submission?.related_scans_approved;
  switch (scanType) {
    case SubmissionScanImageType.t1:
      // Contrast scans are usually treated as bad quality scans.
      if (submission.contrast) {
        return SubmissionScanQuality.worst;
      }
      return typeof submission.scan_quality === 'number' ? submission.scan_quality : 5;
    case SubmissionScanImageType.t2:
      return typeof relatedScansApproved?.T2?.qs === 'number' ? relatedScansApproved.T2.qs : 5;
    case SubmissionScanImageType.flair:
      return typeof relatedScansApproved?.Flair?.qs === 'number' ? relatedScansApproved.Flair.qs : 5;
    case SubmissionScanImageType.tof:
      return typeof relatedScansApproved?.TOF?.qs === 'number' ? relatedScansApproved.TOF.qs : 5;
    case SubmissionScanImageType.swi:
      return typeof relatedScansApproved?.SWI?.qs === 'number' ? relatedScansApproved.SWI.qs : 5;
    default:
      // If no scan type provided the report can be generated regardless of the scans.
      // Provide the best quality to avoid blocking the report generation.
      return 0;
  }
}

/**
 * Transforms the submission data to the hash map with the scan quality for each scan type.
 */
export const submissionToScanQualityHashMap = (submission: Submission): {[key in SubmissionScanImageType]: SubmissionScanQuality} => {
  return {
    [SubmissionScanImageType.t1]: getScanQualityForScanType(submission, SubmissionScanImageType.t1),
    [SubmissionScanImageType.t2]: getScanQualityForScanType(submission, SubmissionScanImageType.t2),
    [SubmissionScanImageType.flair]: getScanQualityForScanType(submission, SubmissionScanImageType.flair),
    [SubmissionScanImageType.tof]: getScanQualityForScanType(submission, SubmissionScanImageType.tof),
    [SubmissionScanImageType.swi]: getScanQualityForScanType(submission, SubmissionScanImageType.swi),
  }
}

/**
 * Extracts the scan status for the provided submission and scan type.
 * Lacking data will be considered as an unknown error.
 */
export const getScanStatusForScanType = (submission: Submission, scanType: SubmissionScanImageType): SubmissionDetailedStatus => {
  // If the submission is not provided, consider it as an unknown error.
  if (!submission) {
    return SubmissionDetailedStatus.errorUnknown;
  }

  const relatedScansApproved = submission?.related_scans_approved;
  switch (scanType) {
    case SubmissionScanImageType.t1:
      return submission.status_description || SubmissionDetailedStatus.errorUnknown;
    case SubmissionScanImageType.t2:
      return relatedScansApproved?.T2?.status_description || SubmissionDetailedStatus.errorUnknown;
    case SubmissionScanImageType.flair:
      return relatedScansApproved?.Flair?.status_description || SubmissionDetailedStatus.errorUnknown;
    case SubmissionScanImageType.tof:
      return relatedScansApproved?.TOF?.status_description || SubmissionDetailedStatus.errorUnknown;
    case SubmissionScanImageType.swi:
      return relatedScansApproved?.SWI?.status_description || SubmissionDetailedStatus.errorUnknown;
    default:
      return SubmissionDetailedStatus.errorUnknown;
  }
}
